import axios from "axios";
import {useCallback, useEffect, useState} from "react";
import {GetDataApi} from "../components/types/GetDataApi";
import {useParams} from "react-router-dom";
import {ResponseDataFunction} from "../components/types/ResponseDataFunction";
import {getGatewayUrl} from "../components/EnvironmentConfiguration";
import {DividendConfig, emptyDividendConfig} from "../domain/assetmanager/DividendConfig";
import {DividendCurrencyConfig, emptyDividendCurrencyConfig} from "../domain/assetmanager/DividendCurrencyConfig";
import {RequestDataFunction} from "../components/types/RequestDataFunction";
import {DividendPaymentDateConfig, emptyDividendPaymentDateConfig} from "../domain/assetmanager/DividendPaymentDateConfig";
import {CashDividendSource} from "../domain/dividend/CashDividendSource";
import {DividendFilterConfig, emptyDividendFilterConfig} from "../domain/assetmanager/DividendFilterConfig";
import {RefreshDataFunction} from "../components/types/RefreshDataFunction";

export function useGetDividendConfiguration(): RefreshDataFunction<DividendConfig> {
    const [responseData, setResponseData] = useState<DividendConfig>(emptyDividendConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const apiFunction = useCallback(async() => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config');
                setResponseData(response.data as DividendConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => { void apiFunction() }, [ apiFunction ])

    return { data: responseData, loading, error, apiFunction: apiFunction };
}

export function useSaveDividendConfiguration(): ResponseDataFunction<DividendConfig, DividendConfig> {
    const [responseData, setResponseData] = useState<DividendConfig>(emptyDividendConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (data: DividendConfig) => {
            setLoading(true);
            const body = JSON.stringify(data);
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            try {
                const response = await axios.post(getGatewayUrl() + '/dividends/v1/config', body, config);
                setResponseData(response.data as DividendConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}

export function useGetDividendCurrencyConfiguration(): GetDataApi<DividendCurrencyConfig> {
    const { currency } = useParams();
    const [responseData, setResponseData] = useState<DividendCurrencyConfig>(emptyDividendCurrencyConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/currency/' + currency);
                setResponseData(response.data as DividendCurrencyConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [ currency ]);
    return { data: responseData, loading, error };
}

export function useGetDividendCurrencyConfigurations(): GetDataApi<DividendCurrencyConfig[]> {
    const [responseData, setResponseData] = useState<DividendCurrencyConfig[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/currency');
                setResponseData(response.data as DividendCurrencyConfig[]);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, []);
    return { data: responseData, loading, error };
}

export function useDeleteDividendCurrencyConfiguration(): RequestDataFunction<number> {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const  apiFunction = useCallback(async (id: number) => {
        setLoading(true);
        try {
            await axios.delete(getGatewayUrl() + '/dividends/v1/config/currency/' + id);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, []);
    return { apiFunction: apiFunction, loading, error };
}

export function useCreateDividendCurrencyDefaultConfiguration(): ResponseDataFunction<string, DividendCurrencyConfig> {
    const [responseData, setResponseData] = useState<DividendCurrencyConfig>(emptyDividendCurrencyConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (name: string) => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/currency/default/' + name);
                setResponseData(response.data as DividendCurrencyConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}

export function useSaveDividendCurrencyConfiguration(): ResponseDataFunction<DividendCurrencyConfig, DividendCurrencyConfig> {
    const [responseData, setResponseData] = useState<DividendCurrencyConfig>(emptyDividendCurrencyConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (data: DividendCurrencyConfig) => {
            setLoading(true);
            const body = JSON.stringify(data);
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            try {
                const response = await axios.post(getGatewayUrl() + '/dividends/v1/config/currency', body, config);
                setResponseData(response.data as DividendCurrencyConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}

export function useGetDividendPaymentDateConfigurations(): GetDataApi<DividendPaymentDateConfig[]> {
    const [responseData, setResponseData] = useState<DividendPaymentDateConfig[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/payment/date');
                setResponseData(response.data as DividendPaymentDateConfig[]);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, []);
    return { data: responseData, loading, error };
}

export function useGetDividendPaymentDateConfiguration(): GetDataApi<DividendPaymentDateConfig> {
    const { id } = useParams();
    const [responseData, setResponseData] = useState<DividendPaymentDateConfig>(emptyDividendPaymentDateConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/payment/date/' + id);
                setResponseData(response.data as DividendPaymentDateConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [ id ]);
    return { data: responseData, loading, error };
}

export function useDeleteDividendPaymentDateConfiguration(): RequestDataFunction<number> {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const  apiFunction = useCallback(async (id: number) => {
        setLoading(true);
        try {
            await axios.delete(getGatewayUrl() + '/dividends/v1/config/payment/date/' + id);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, []);
    return { apiFunction: apiFunction, loading, error };
}

export function useCreateDividendPaymentDateDefaultConfiguration(): ResponseDataFunction<CashDividendSource, DividendPaymentDateConfig> {
    const [responseData, setResponseData] = useState<DividendPaymentDateConfig>(emptyDividendPaymentDateConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (name: CashDividendSource) => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/payment/date/default/' + name);
                setResponseData(response.data as DividendPaymentDateConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}

export function useSaveDividendPaymentDateConfiguration(): ResponseDataFunction<DividendPaymentDateConfig, DividendPaymentDateConfig> {
    const [responseData, setResponseData] = useState<DividendPaymentDateConfig>(emptyDividendPaymentDateConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (data: DividendPaymentDateConfig) => {
            setLoading(true);
            const body = JSON.stringify(data);
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            setError(null);
            try {
                const response = await axios.post(getGatewayUrl() + '/dividends/v1/config/payment/date', body, config);
                setResponseData(response.data as DividendPaymentDateConfig);
            } catch (err) {
                if (err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response) {
                    setError(String(err.response.data));
                } else {
                    setError(err instanceof Error ? err.message : String(err));
                }
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}

export function useGetDividendFilterConfigurations(): GetDataApi<DividendFilterConfig[]> {
    const [responseData, setResponseData] = useState<DividendFilterConfig[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/filter');
                setResponseData(response.data as DividendFilterConfig[]);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, []);
    return { data: responseData, loading, error };
}

export function useGetDividendFilterConfiguration(): GetDataApi<DividendFilterConfig> {
    const { id } = useParams();
    const [responseData, setResponseData] = useState<DividendFilterConfig>(emptyDividendFilterConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/filter/' + id);
                setResponseData(response.data as DividendFilterConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [ id ]);
    return { data: responseData, loading, error };
}
export function useDeleteDividendFilterConfiguration(): RequestDataFunction<number> {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const  apiFunction = useCallback(async (id: number) => {
        setLoading(true);
        try {
            await axios.delete(getGatewayUrl() + '/dividends/v1/config/filter/' + id);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, []);
    return { apiFunction: apiFunction, loading, error };
}

export function useCreateDividendFilterDefaultConfiguration(): ResponseDataFunction<CashDividendSource, DividendFilterConfig> {
    const [responseData, setResponseData] = useState<DividendFilterConfig>(emptyDividendFilterConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (name: CashDividendSource) => {
            setLoading(true);
            try {
                const response = await axios.get(getGatewayUrl() + '/dividends/v1/config/filter/default/' + name);
                setResponseData(response.data as DividendFilterConfig);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}

export function useSaveDividendFilterConfiguration(): ResponseDataFunction<DividendFilterConfig, DividendFilterConfig> {
    const [responseData, setResponseData] = useState<DividendFilterConfig>(emptyDividendFilterConfig());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getFunction = useCallback(
        async (data: DividendFilterConfig) => {
            setLoading(true);
            const body = JSON.stringify(data);
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            try {
                const response = await axios.post(getGatewayUrl() + '/dividends/v1/config/filter', body, config);
                setResponseData(response.data as DividendFilterConfig);
            } catch (err) {
                if (err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response) {
                    setError(String(err.response.data));
                } else {
                    setError(err instanceof Error ? err.message : String(err));
                }
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return { data: responseData, loading, error, apiFunction: getFunction };
}


